import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import t from 'modules/i18n/intl';
import styled from 'styled-components';

import Button from '@atlaskit/button/new';
import ModalDialog, {
  ModalBody,
  ModalTitle,
  ModalHeader,
  ModalFooter,
} from '@atlaskit/modal-dialog';
import Spinner from '@atlaskit/spinner';
import { deleteCurrentQuote, fetchQuotesPost } from '../duck/actions/orders';
import { getCurrentMACContactDetails } from '../duck/selectors';
import { State } from '../../../model/State';
import { messages } from '../messages';

const SpinnerWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: baseline;
  height: 150px;
`;

const LoadingState = () => (
  <>
    <ModalBody>
      <SpinnerWrapper>
        <Spinner size="large" />
      </SpinnerWrapper>
    </ModalBody>
  </>
);

const ErrorState = ({ error, onCancel }: { error: string; onCancel: (e: any) => void }) => (
  <>
    <ModalBody>
      <span>
        <FormattedMessage id={error} />
      </span>
    </ModalBody>
    <ModalFooter>
      <Button appearance="default" onClick={onCancel}>
        <FormattedMessage {...messages.ordersModalClose} />
      </Button>
    </ModalFooter>
  </>
);

const SuccessState = ({ message, onCancel }: { message: string; onCancel: (e: any) => void }) => {
  const dispatch = useDispatch();
  const refetchQuotes = (e) => {
    dispatch(fetchQuotesPost(10, 0));
    onCancel(e);
  };
  return (
    <>
      <ModalBody>
        <span>{message}</span>
      </ModalBody>
      <ModalFooter>
        <Button appearance="default" onClick={refetchQuotes} testId="delete-quote-modal-close-cta">
          <FormattedMessage {...messages.ordersModalClose} />
        </Button>
      </ModalFooter>
    </>
  );
};

const DefaultState = ({
  onSubmit,
  onCancel,
}: {
  onSubmit: (e) => void;
  onCancel: (e: any) => void;
}) => (
  <>
    <ModalBody>
      <span>
        <FormattedMessage {...messages.ordersModalDeleteText} />
      </span>
    </ModalBody>
    <ModalFooter>
      <Button
        appearance="warning"
        autoFocus
        onClick={onSubmit}
        testId="delete-quote-modal-delete-cta"
      >
        <FormattedMessage {...messages.ordersModalDelete} />
      </Button>
      <Button appearance="default" onClick={onCancel}>
        <FormattedMessage {...messages.ordersModalClose} />
      </Button>
    </ModalFooter>
  </>
);

interface Props {
  rowItem: any;
  onCancel: (e: any) => void;
}

const DeleteQuoteModalBody = ({ rowItem, onCancel }: Props) => {
  const { id, orderNumber } = rowItem;

  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const contactDetails = useSelector(
    (state: State) =>
      getCurrentMACContactDetails(state).ifSuccess(
        (currentContact) => currentContact?.contactDetails || null,
      ) || null,
  );

  const displayName = contactDetails
    ? `${contactDetails.firstName} ${contactDetails.lastName}`
    : '';

  const handleDeleteQuote = () => {
    setIsLoading(true);

    try {
      dispatch(deleteCurrentQuote(id, displayName));
      setSuccessMessage(
        t('mac.orders.table.table-item.action.delete-modal.success', { orderNumber }),
      );
    } catch (e) {
      setErrorMessage(e.message);
    }
    setIsLoading(false);
  };

  if (isLoading) {
    return <LoadingState />;
  }

  if (errorMessage) {
    return <ErrorState error={errorMessage} onCancel={onCancel} />;
  }

  if (successMessage) {
    return <SuccessState message={successMessage} onCancel={onCancel} />;
  }

  return <DefaultState onSubmit={handleDeleteQuote} onCancel={onCancel} />;
};

const DeleteQuoteModal = (props: Props) => {
  return (
    <ModalDialog
      autoFocus
      onClose={props.onCancel}
      shouldCloseOnOverlayClick
      shouldCloseOnEscapePress
      width="small"
      testId="delete-quote-modal"
    >
      <ModalHeader>
        <ModalTitle appearance="warning">
          <span>
            <FormattedMessage {...messages.ordersModalDeleteTitle} />
          </span>
        </ModalTitle>
      </ModalHeader>
      <DeleteQuoteModalBody {...props} />
    </ModalDialog>
  );
};

export default DeleteQuoteModal;
